import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://whitegloveeventconcierge.com';
const DEFAULT_OG = `${SITE_URL}/assets/og-image.jpg`;
const DEFAULT_TITLE = 'White Glove Event Concierge | Nashville Event Advisory';
const DEFAULT_DESC =
  'Strategic event consulting for high-value events in Nashville. Budget strategy, vendor alignment, and contract guidance from $300.';

/**
 * Per-page SEO. Sets <title>, meta description, canonical URL, Open Graph
 * and Twitter Card tags, and optional JSON-LD structured data.
 */
export const SEO = ({ title, description, image, jsonLd, noIndex = false }) => {
  const { pathname } = useLocation();
  const url = `${SITE_URL}${pathname === '/' ? '' : pathname}`;
  const finalTitle = title || DEFAULT_TITLE;
  const finalDesc = description || DEFAULT_DESC;
  const finalImage = image || DEFAULT_OG;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <link rel="canonical" href={url} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="White Glove Event Concierge" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

// Reusable JSON-LD blocks
export const ORGANIZATION_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'White Glove Event Concierge',
  url: SITE_URL,
  logo: `${SITE_URL}/assets/wg-logo.jpg`,
  description:
    'Strategic event consulting for high-value events in Nashville. Budget strategy, vendor alignment, and contract guidance.',
  email: 'info@whitegloveeventconcierge.com',
  telephone: '+1-615-669-0576',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Nashville',
    addressRegion: 'TN',
    addressCountry: 'US',
  },
};

export const LOCAL_BUSINESS_LD = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#localbusiness`,
  name: 'White Glove Event Concierge',
  url: SITE_URL,
  image: DEFAULT_OG,
  logo: `${SITE_URL}/assets/wg-logo.jpg`,
  email: 'info@whitegloveeventconcierge.com',
  telephone: '+1-615-669-0576',
  priceRange: '$300 – $$$$',
  description:
    'Strategic event consulting and concierge advisory for weddings, corporate events, galas, and private celebrations. We provide budget strategy, vendor alignment, contract guidance, and oversight — without acting as a full-service planner.',
  areaServed: [
    { '@type': 'City', name: 'Nashville' },
    { '@type': 'AdministrativeArea', name: 'Tennessee' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Nashville',
    addressRegion: 'TN',
    addressCountry: 'US',
  },
};

export const SERVICE_LD = (service) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: service.name,
  provider: { '@id': `${SITE_URL}/#localbusiness` },
  areaServed: { '@type': 'City', name: 'Nashville' },
  description: service.description,
  offers: service.price
    ? {
        '@type': 'Offer',
        price: service.price,
        priceCurrency: 'USD',
        url: service.url || `${SITE_URL}/consulting`,
      }
    : undefined,
});

export const articleLd = ({ title, description, image, datePublished, author }) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: title,
  description,
  image,
  datePublished,
  author: { '@type': 'Person', name: author || 'White Glove Event Concierge' },
  publisher: {
    '@type': 'Organization',
    name: 'White Glove Event Concierge',
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/assets/wg-logo.jpg` },
  },
});

export const faqLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

export { SITE_URL };
